import React from "react";
import BasicInfoSection from "./BasicInfoSection";
import ZoneAssignmentSection from "./ZoneAssignmentSection";
import MediaSection from "./MediaSection";

/**
 * Complete species form component
 * Combines all form sections and action buttons
 * 
 * @param {number} selectedItem - ID of selected species (null for new)
 * @param {Object} newSpecieData - Form data {name, category_name, description}
 * @param {Function} setNewSpecieData - Setter for form data
 * @param {string} categorySearchQuery - Category search query
 * @param {Function} setCategorySearchQuery - Setter for category search
 * @param {boolean} isCategoryDropdownOpen - Category dropdown state
 * @param {Function} setIsCategoryDropdownOpen - Setter for category dropdown
 * @param {Array} filteredCategories - Filtered categories
 * @param {string} speciesType - Selected category
 * @param {Function} handleCategorySelect - Category selection handler
 * @param {Function} setSpeciesType - Setter for species type
 * @param {Object} categoryDropdownRef - Category dropdown ref
 * @param {Array} selectedSpeciesZones - Selected zone IDs
 * @param {Function} setSelectedSpeciesZones - Setter for zones
 * @param {boolean} isSpeciesZoneDropdownOpen - Zone dropdown state
 * @param {Function} setIsSpeciesZoneDropdownOpen - Setter for zone dropdown
 * @param {Array} areasList - Available areas
 * @param {Function} handleSpeciesZoneToggle - Zone toggle handler
 * @param {Object} speciesZoneDropdownRef - Zone dropdown ref
 * @param {Array} specieImageData - Image data
 * @param {Function} handleDeleteImage - Delete image handler
 * @param {Function} openAddImageModal - Open image modal handler
 * @param {Object} specieSound - Sound data
 * @param {Function} openAddAudioModal - Open audio modal handler
 * @param {Function} handleDeleteSound - Delete sound handler
 * @param {Function} handleSave - Save handler
 * @param {Function} handleDeleteSpecies - Delete species handler
 * 
 * @param {string} habitatSearchQuery
 * @param {Function} setHabitatSearchQuery
 * @param {boolean} isHabitatDropdownOpen
 * @param {Function} setIsHabitatDropdownOpen
 * @param {array} filteredHabitats
 * @param {string} speciesHabitat
 * @param {Function} handleHabitatSelect
 * @param {Function} setSpeciesHabitat
 * @param {Object} habitatDropdownRef
 * 
 */
export default function SpeciesForm({
    selectedItem,
    newSpecieData,
    setNewSpecieData,
    categorySearchQuery,
    setCategorySearchQuery,
    isCategoryDropdownOpen,
    setIsCategoryDropdownOpen,
    filteredCategories,
    speciesType,
    handleCategorySelect,
    setSpeciesType,
    categoryDropdownRef,
    selectedSpeciesZones,
    setSelectedSpeciesZones,
    isSpeciesZoneDropdownOpen,
    setIsSpeciesZoneDropdownOpen,
    areasList,
    handleSpeciesZoneToggle,
    speciesZoneDropdownRef,
    specieImageData,
    handleDeleteImage,
    openAddImageModal,
    specieSound,
    openAddAudioModal,
    handleDeleteSound,
    handleSave,
    handleDeleteSpecies,

    //Habitat
    habitatSearchQuery,
    setHabitatSearchQuery,
    isHabitatDropdownOpen,
    setIsHabitatDropdownOpen,
    filteredHabitats,
    speciesHabitat,
    handleHabitatSelect,
    setSpeciesHabitat,
    habitatDropdownRef,

}) {
    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <h2 className="font-heading text-2xl font-bold text-charcoal">
                    {selectedItem ? "Redigera art" : "Lägg till ny art"}
                </h2>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-6">
                    <BasicInfoSection
                        newSpecieData={newSpecieData}
                        setNewSpecieData={setNewSpecieData}
                        categorySearchQuery={categorySearchQuery}
                        setCategorySearchQuery={setCategorySearchQuery}
                        isCategoryDropdownOpen={isCategoryDropdownOpen}
                        setIsCategoryDropdownOpen={setIsCategoryDropdownOpen}
                        filteredCategories={filteredCategories}
                        speciesType={speciesType}
                        handleCategorySelect={handleCategorySelect}
                        setSpeciesType={setSpeciesType}
                        categoryDropdownRef={categoryDropdownRef}

                        habitatSearchQuery={habitatSearchQuery}
                        setHabitatSearchQuery={setHabitatSearchQuery}
                        isHabitatDropdownOpen={isHabitatDropdownOpen}
                        setIsHabitatDropdownOpen={setIsHabitatDropdownOpen}
                        filteredHabitats={filteredHabitats}
                        speciesHabitat={speciesHabitat}
                        handleHabitatSelect={handleHabitatSelect}
                        setSpeciesHabitat={setSpeciesHabitat}
                        habitatDropdownRef={habitatDropdownRef}
                    />

                    <ZoneAssignmentSection
                        selectedSpeciesZones={selectedSpeciesZones}
                        setSelectedSpeciesZones={setSelectedSpeciesZones}
                        isSpeciesZoneDropdownOpen={isSpeciesZoneDropdownOpen}
                        setIsSpeciesZoneDropdownOpen={setIsSpeciesZoneDropdownOpen}
                        areasList={areasList}
                        handleSpeciesZoneToggle={handleSpeciesZoneToggle}
                        speciesZoneDropdownRef={speciesZoneDropdownRef}
                    />

                    <MediaSection
                        specieImageData={specieImageData}
                        handleDeleteImage={handleDeleteImage}
                        openAddImageModal={openAddImageModal}
                        specieSound={specieSound}
                        openAddAudioModal={openAddAudioModal}
                        handleDeleteSound={handleDeleteSound}
                    />

                    {/* Save and Delete Buttons */}
                    <div className="space-y-3 pt-6 mt-6 border-t border-gray-200 sticky bottom-0 bg-white pb-2">
                        <button
                            onClick={handleSave}
                            className="w-full bg-forest hover:bg-leaf text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300 text-sm sm:text-base"
                        >
                            {selectedItem ? "Spara ändringar" : "Lägg till art"}
                        </button>
                        
                        {selectedItem && (
                            <button
                                onClick={handleDeleteSpecies}
                                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300 text-sm sm:text-base"
                            >
                                Radera art
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
